import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { displayErrorMessage } from 'redux/notificationBar/notificationBarSlice';
import { getIncidents, updateIncidentStatus } from 'api/canvms/incidents';
import type { Incident, IncidentCounts, IncidentFilters, IncidentStatus } from '../types/canvision.types';

export function useIncidents(filters?: IncidentFilters) {
  const dispatch = useDispatch();
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<IncidentCounts>({
    new: 0,
    reviewing: 0,
    dispatched: 0,
    resolved: 0,
    closed: 0,
    dismissed: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialLoadDone = useRef(false);

  const fetchIncidents = useCallback(async (isRefresh = false) => {
    try {
      // Only show loading on initial load, not refreshes
      if (!isRefresh && !initialLoadDone.current) {
        setLoading(true);
      }
      setError(null);
      const response = await getIncidents(filters);
      setIncidents(response.data?.incidents || []);
      setTotal(response.data?.total || 0);
      setCounts(response.data?.counts || { new: 0, reviewing: 0, dispatched: 0, resolved: 0, closed: 0, dismissed: 0 });
      initialLoadDone.current = true;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to fetch incidents';
      console.error('[CANVMS/fetchIncidents] Error fetching incidents:', err);
      setError(message);
      dispatch(displayErrorMessage(message));
    } finally {
      setLoading(false);
    }
  }, [
    filters?.status,
    filters?.severity,
    filters?.cameraId,
    filters?.siteId,
    filters?.from,
    filters?.to,
    filters?.limit,
    filters?.offset,
    dispatch,
  ]);

  useEffect(() => {
    initialLoadDone.current = false;
    fetchIncidents(false);
  }, [fetchIncidents]);

  const updateStatus = useCallback(async (incidentId: number, status: IncidentStatus, notes?: string) => {
    try {
      await updateIncidentStatus(incidentId, status, notes);
      // Refresh the list after update
      await fetchIncidents(true);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to update incident status';
      console.error('[CANVMS/updateIncidentStatus] Error updating incident status:', err);
      dispatch(displayErrorMessage(message));
      throw err;
    }
  }, [fetchIncidents, dispatch]);

  const refetch = useCallback(() => fetchIncidents(true), [fetchIncidents]);

  return {
    incidents,
    total,
    counts,
    loading,
    error,
    updateStatus,
    refetch,
  };
}
